import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintIcon from '@mui/icons-material/Print';
import {
  findTicketByReservationIdExact,
  getReservations,
  type ReservationListItem,
  type TicketReservationByPnrData,
} from '../../../services/reservationsApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  buildRowNumberMap,
  formatReservationCurrency,
  formatReservationDate,
  mapRowToTicketDetailFallback,
  RESERVATION_STATUS_COLOR_MAP,
} from '../utils/reservationMapper';
import { useTranslation } from 'react-i18next';

const ReservationList: React.FC = () => {
  const { t } = useTranslation('other');
  type SortKey = 'date' | 'guestCount' | 'amount';
  const { getBranchName } = useAuth();
  const [rows, setRows] = useState<ReservationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [detailLoadingIds, setDetailLoadingIds] = useState<Set<number>>(new Set());
  const [detailsById, setDetailsById] = useState<Record<number, TicketReservationByPnrData | null>>({});
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [rowMenuTargetId, setRowMenuTargetId] = useState<number | null>(null);

  useEffect(() => {
    const loadReservations = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getReservations({
          type: 'ticket',
          page: 1,
          pageSize: 200,
        });
        setRows(response.rows);
      } catch (err) {
        console.error('Failed to load reservation list:', err);
        setError(t('reservations.list.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadReservations();
  }, [t]);

  const toggleExpand = async (row: ReservationListItem) => {
    const isExpanded = expandedRows.has(row.id);
    const nextExpanded = new Set(expandedRows);
    if (isExpanded) {
      nextExpanded.delete(row.id);
      setExpandedRows(nextExpanded);
      return;
    }

    nextExpanded.add(row.id);
    setExpandedRows(nextExpanded);

    if (detailsById[row.id] !== undefined) return;

    const nextLoading = new Set(detailLoadingIds);
    nextLoading.add(row.id);
    setDetailLoadingIds(nextLoading);

    try {
      const byReservationId = await findTicketByReservationIdExact(row.reservationId);
      setDetailsById((prev) => ({
        ...prev,
        [row.id]: byReservationId || mapRowToTicketDetailFallback(row, row.reservationId),
      }));
    } catch (err) {
      console.error('Failed to load reservation details:', err);
      setDetailsById((prev) => ({
        ...prev,
        [row.id]: mapRowToTicketDetailFallback(row, row.reservationId),
      }));
    } finally {
      setDetailLoadingIds((prev) => {
        const set = new Set(prev);
        set.delete(row.id);
        return set;
      });
    }
  };

  const orderedRows = useMemo(
    () => {
      const sorted = [...rows].sort((a, b) => {
        if (sortKey === 'date') {
          const aTime = new Date(a.reservationDate || a.createdAt || 0).getTime();
          const bTime = new Date(b.reservationDate || b.createdAt || 0).getTime();
          return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
        }

        if (sortKey === 'guestCount') {
          const aGuests = Number(a.guestCount ?? a.ticketDetail?.guests?.length ?? 0);
          const bGuests = Number(b.guestCount ?? b.ticketDetail?.guests?.length ?? 0);
          return sortDirection === 'asc' ? aGuests - bGuests : bGuests - aGuests;
        }

        const aAmount = Number(a.totalAmount || 0);
        const bAmount = Number(b.totalAmount || 0);
        return sortDirection === 'asc' ? aAmount - bAmount : bAmount - aAmount;
      });

      return sorted;
    },
    [rows, sortDirection, sortKey]
  );
  const orderedRowNumbers = useMemo(() => buildRowNumberMap(orderedRows), [orderedRows]);
  const getGuestTypeLabel = (type?: string): string => {
    const value = (type || '').trim().toLowerCase();
    if (value === 'adult') return t('reservations.list.guestTypes.adult');
    if (value === 'child') return t('reservations.list.guestTypes.child');
    if (value === 'free') return t('reservations.list.guestTypes.free');
    return type || '-';
  };
  const buildGuestRows = (details: TicketReservationByPnrData) => {
    const guests = details.guests || [];
    const lineItems = details.lineItems || [];
    const paidUnits = lineItems.flatMap((line) => {
      const quantity = Number(line.miktar ?? line.quantity ?? 1) || 1;
      const unitPrice = Number(line.birimFiyat ?? line.unitPrice ?? 0) || 0;
      const productName = line.urunAdi || line.productName || '';
      return Array.from({ length: Math.max(1, quantity) }, () => ({ productName, unitPrice }));
    });

    let paidCursor = 0;
    return guests.map((guest) => {
      const guestName = `${guest.firstName || ''} ${guest.lastName || ''}`.trim() || '-';
      const typeLabel = getGuestTypeLabel(guest.type);

      if ((guest.type || '').toLowerCase() === 'free') {
        return {
          guestName,
          ticketType: t('reservations.list.guestTypes.free'),
          amount: 0,
        };
      }

      const paidUnit = paidUnits[paidCursor];
      if (paidUnit) paidCursor += 1;
      return {
        guestName,
        ticketType: paidUnit?.productName || `${typeLabel} Bileti`,
        amount: paidUnit?.unitPrice || 0,
      };
    });
  };
  const handleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }

    setSortKey(nextKey);
    // Ilk tiklamada istenen varsayilan: Tarih yakin->uzak, Kisi/Tutar coktan->aza.
    setSortDirection('desc');
  };
  const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
    event.stopPropagation();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuTargetId(rowId);
  };
  const handleCloseRowMenu = () => {
    setRowMenuAnchorEl(null);
    setRowMenuTargetId(null);
  };
  const handlePrintReservation = async () => {
    if (!rowMenuTargetId) return;

    const row = orderedRows.find((item) => item.id === rowMenuTargetId);
    if (!row) {
      handleCloseRowMenu();
      return;
    }

    let details = detailsById[row.id];
    if (!details) {
      try {
        details = await findTicketByReservationIdExact(row.reservationId);
      } catch {
        details = null;
      }
      if (!details) {
        details = mapRowToTicketDetailFallback(row, row.reservationId);
      }
    }

    const detailData = details || mapRowToTicketDetailFallback(row, row.reservationId);
    const guestRows = detailData ? buildGuestRows(detailData) : [];

    const escapeHtml = (value: string) =>
      (value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const guestRowsHtml =
      guestRows.length > 0
        ? guestRows
            .map(
              (g, idx) =>
                `<tr><td>${idx + 1}</td><td>${escapeHtml(g.guestName)}</td><td>${escapeHtml(g.ticketType)}</td><td style="text-align:right;">${escapeHtml(
                  formatReservationCurrency(g.amount)
                )}</td></tr>`
            )
            .join('')
        : `<tr><td colspan="4">${t('reservations.list.print.noGuestInfo')}</td></tr>`;

    const html = `
      <!doctype html>
      <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${t('reservations.list.print.title')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
          h2 { margin: 0 0 14px 0; font-size: 20px; }
          .meta { margin-bottom: 14px; font-size: 13px; color: #475569; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #e2e8f0; padding: 8px 10px; font-size: 13px; }
          th { background: #f8fafc; text-align: left; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h2>${t('reservations.list.print.detailTitle')}</h2>
        <div class="meta">${t('reservations.list.print.reservationNo')}: ${escapeHtml(row.reservationId)} | ${t('reservations.list.print.date')}: ${escapeHtml(
      formatReservationDate(row.reservationDate)
    )} | ${t('reservations.list.print.customer')}: ${escapeHtml(row.customerName || '-')}</div>
        <table>
          <thead>
            <tr><th>${t('reservations.list.print.order')}</th><th>${t('reservations.list.print.personName')}</th><th>${t('reservations.list.print.ticketType')}</th><th style="text-align:right;">${t('reservations.list.print.amount')}</th></tr>
          </thead>
          <tbody>${guestRowsHtml}</tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      handleCloseRowMenu();
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);

    handleCloseRowMenu();
  };
  const sortLabelSx = {
    color: '#fff',
    fontWeight: 700,
    '&.MuiTableSortLabel-root': {
      color: '#fff',
    },
    '&.Mui-active': {
      color: '#fff',
    },
    '&:hover': {
      color: '#fff',
    },
    '& .MuiTableSortLabel-icon': {
      color: '#fff !important',
      opacity: 0.9,
      fontSize: '0.95rem',
    },
  } as const;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">{t('reservations.list.title')}</Typography>
        <Chip label={t('reservations.list.total', { count: rows.length })} color="primary" variant="outlined" />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
          <Table stickyHeader aria-label={t('reservations.list.tableAriaLabel')}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700, width: 52 }}>{t('reservations.list.table.order')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700, width: 56 }} />
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortKey === 'date'}
                    direction={sortKey === 'date' ? sortDirection : 'desc'}
                    onClick={() => handleSort('date')}
                    sx={sortLabelSx}
                  >
                    {t('reservations.list.table.date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.list.table.customer')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.list.table.phone')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.list.table.reservationNo')}</TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortKey === 'guestCount'}
                    direction={sortKey === 'guestCount' ? sortDirection : 'desc'}
                    onClick={() => handleSort('guestCount')}
                    sx={sortLabelSx}
                  >
                    {t('reservations.list.table.guestCount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.list.table.branch')}</TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortKey === 'amount'}
                    direction={sortKey === 'amount' ? sortDirection : 'desc'}
                    onClick={() => handleSort('amount')}
                    sx={sortLabelSx}
                  >
                    {t('reservations.list.table.amount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                  {t('reservations.list.table.status')}
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700, width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedRows.map((row) => (
                <React.Fragment key={`${row.reservationType}-${row.id}`}>
                  <TableRow
                    sx={{
                      '&:hover': { backgroundColor: '#f9f9f9' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{orderedRowNumbers[row.id]}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(row)}
                        sx={{
                          color: 'text.secondary',
                          width: 24,
                          height: 24,
                        }}
                      >
                        {expandedRows.has(row.id) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatReservationDate(row.reservationDate)}</TableCell>
                    <TableCell>{row.customerName || '-'}</TableCell>
                    <TableCell>{row.phone || '-'}</TableCell>
                    <TableCell component="th" scope="row">
                      {row.reservationId}
                    </TableCell>
                    <TableCell align="center">
                      {row.guestCount ?? row.ticketDetail?.guests?.length ?? '-'}
                    </TableCell>
                    <TableCell>{getBranchName(row.branchId) || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {formatReservationCurrency(row.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.statusDisplay || row.status}
                        color={RESERVATION_STATUS_COLOR_MAP[row.status] || 'default'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={(event) => handleOpenRowMenu(event, row.id)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: expandedRows.has(row.id) ? '1px solid rgba(0,0,0,0.12)' : 0 }} colSpan={11}>
                      <Collapse in={expandedRows.has(row.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 1, p: 2, borderRadius: 1, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                          {detailLoadingIds.has(row.id) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2">{t('reservations.list.loadingDetails')}</Typography>
                            </Box>
                          ) : detailsById[row.id] ? (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                {t('reservations.list.detail.title')}
                              </Typography>
                              <Box sx={{ display: 'grid', gap: 0.75 }}>
                                {(detailsById[row.id]?.guests || []).length > 0 ? (
                                  <Table size="small" sx={{ mt: 0.5 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(0, 0, 0, 0.03)', width: 56 }}>{t('reservations.list.print.order')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('reservations.list.print.personName')}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('reservations.list.print.ticketType')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>{t('reservations.list.print.amount')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {buildGuestRows(detailsById[row.id]!).map((guestRow, guestIndex) => (
                                        <TableRow key={`${row.id}-guest-row-${guestIndex}`}>
                                          <TableCell sx={{ fontWeight: 700 }}>{guestIndex + 1}</TableCell>
                                          <TableCell>{guestRow.guestName}</TableCell>
                                          <TableCell>{guestRow.ticketType}</TableCell>
                                          <TableCell align="right">{formatReservationCurrency(guestRow.amount)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography variant="body2">
                                    {t('reservations.list.detail.noGuestInfo')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('reservations.list.detail.noDetail')}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {t('reservations.list.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handlePrintReservation}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('reservations.list.actions.print')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ReservationList;